// The phone tab bar (below md), from design/isomorphic/src/layouts/bottom-tabs.tsx: borrowed from
// the iOS app's tabs so the phone browser and the phone app feel like one product. "Answer"
// opens the oldest open question's run and carries a dot while one is waiting.
import { ChatCircleDots, DeviceMobile, Gauge, type Icon, Key, ListChecks } from "@phosphor-icons/react";
import { Link, useLocation } from "react-router";

import { useT } from "../i18n";
import { cn } from "../ui/cn";
import { useAttention } from "./attention";
import { isActive } from "./nav";

interface Tab {
  name: string;
  href: string;
  Icon: Icon;
  dot?: boolean;
  exact?: boolean;
}

export function BottomTabs() {
  const t = useT().shell;
  const { pathname } = useLocation();
  const { questions } = useAttention();
  const oldest = [...questions].sort((a, b) => a.asked_at - b.asked_at)[0];
  const answerHref = oldest ? `/runs/${oldest.run_id}` : "/runs";
  const tabs: Tab[] = [
    { name: t.pages.home.title, href: "/", Icon: Gauge },
    { name: t.pages.runs.title, href: "/runs", Icon: ListChecks },
    { name: t.answer, href: answerHref, Icon: ChatCircleDots, dot: oldest !== undefined, exact: true },
    { name: t.pages.secrets.title, href: "/secrets", Icon: Key },
    { name: t.pages.devices.title, href: "/devices", Icon: DeviceMobile },
  ];
  const answering = oldest !== undefined && pathname === answerHref;
  return (
    <nav
      data-bottom-tabs
      aria-label={t.tabs}
      className="fixed inset-x-0 bottom-0 z-[980] border-t border-muted bg-gray-0/90 px-2 pb-[max(env(safe-area-inset-bottom),8px)] pt-1.5 backdrop-blur-xl dark:bg-gray-50/90 md:hidden"
    >
      <ul className="m-0 grid list-none grid-cols-5 p-0">
        {tabs.map(({ name, href, Icon, dot, exact }) => {
          const on = exact ? answering : isActive(pathname, href) && !(href === "/runs" && answering);
          return (
            <li key={href}>
              <Link to={href} className="flex flex-col items-center gap-0.5 py-1" aria-current={on ? "page" : undefined}>
                <span className={cn("relative rounded-full px-3 py-1", on && "bg-primary-lighter")}>
                  <Icon weight="duotone" className={cn("h-[22px] w-[22px]", on ? "text-primary" : "text-gray-500")} />
                  {dot ? <span className="absolute end-2 top-0.5 h-2 w-2 rounded-full bg-orange ring-2 ring-gray-0 dark:ring-gray-50" /> : null}
                </span>
                <span className={cn("text-[10.5px] font-medium", on ? "text-primary" : "text-gray-500")}>{name}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
